import React, { useMemo } from "react";
import Header from "./Components/Header";
import { CLASS_BOOKING } from "../../assets/asset";
import { TrendingUp } from "lucide-react";
import SessionCard from "./Components/SessionCard";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PageContainer from "./Components/PageContainer";
import MainContent from "./Components/MainContent";
import upcomingIcon from "../../assets/global/upcoming_icon.svg";
import { useQuery } from "@tanstack/react-query";
import { bookingService } from "../../services/booking.service";
import { getClassBookingQueryErrorMessage } from "../../utils/uiErrorMessages";
import {
  getStudentBookingDisplayStatus,
  isPendingBooking,
} from "../../utils/bookingStatus";
import { getClassBookingBasePath } from "../../features/parentFlow/parentFlow";
import { persistBookingSummary } from "./bookingFlowStorage";

const formatBookingDate = (value) => {
  if (!value) return "Date not provided";

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return String(value);

  return parsedDate.toLocaleDateString("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatBookingTime = (booking = {}) =>
  booking?.slot?.time_label || "Time not provided";

const formatBookingDuration = (booking = {}) => {
  const startTime = booking?.slot?.start_time || booking?.slot?.startTime;
  const endTime = booking?.slot?.end_time || booking?.slot?.endTime;
  if (!startTime || !endTime) return "60 minutes";

  const startDate = new Date(startTime);
  const endDate = new Date(endTime);
  if (
    Number.isNaN(startDate.getTime()) ||
    Number.isNaN(endDate.getTime())
  ) {
    return "60 minutes";
  }

  const durationInMinutes = Math.max(
    0,
    Math.round((endDate.getTime() - startDate.getTime()) / 60000)
  );

  return `${durationInMinutes || 60} minutes`;
};

const getRetryBookingId = (booking = {}) =>
  String(booking?.booking_id || booking?.id || "").trim();

const getRetryTutorId = (booking = {}) =>
  String(
    booking?.tutor?.id || booking?.tutor_id || booking?.tutor?.teacher_id || ""
  ).trim();

const buildRetryBookingSummary = (booking = {}, studentName = "") => ({
  bookingId: getRetryBookingId(booking),
  tutorId: getRetryTutorId(booking),
  tutor: booking?.tutor || null,
  heldBooking: booking,
  selectedSlot: booking?.slot || null,
  yearId: booking?.year?.id || booking?.year_id || "",
  studentName: studentName || "Student",
  formattedDate: formatBookingDate(booking?.slot?.date || booking?.slot?.start_time),
  selectedTime: formatBookingTime(booking),
  duration: formatBookingDuration(booking),
  status: booking?.status || "",
  payment_status: booking?.payment_status || "",
  paid_at: booking?.paid_at || "",
  paymentAttemptStatus:
    booking?.paymentAttemptStatus || booking?.payment_attempt_status || "",
});

const mapBookingToSessionCard = (booking, studentName, onRetryPayment) => {
  const bookingId = getRetryBookingId(booking);
  const tutorId = getRetryTutorId(booking);
  const isRetryablePendingBooking =
    isPendingBooking(booking) && Boolean(bookingId && tutorId);

  return {
    id:
      booking?.id ||
      booking?.booking_id ||
      `${booking?.tutor?.id || booking?.tutor?.name || "tutor"}-${
        booking?.slot?.id || booking?.slot?.date || "slot"
      }`,
    tutorName: booking?.tutor?.name || "Tutor",
    image: booking?.tutor?.photoImage || "",
    date: formatBookingDate(booking?.slot?.date || booking?.slot?.start_time),
    time: formatBookingTime(booking),
    student: studentName || "Student",
    status: getStudentBookingDisplayStatus(booking),
    personLabel: "Student",
    actionLabel: isRetryablePendingBooking ? "Retry Payment" : "",
    onAction: isRetryablePendingBooking
      ? () => onRetryPayment(buildRetryBookingSummary(booking, studentName))
      : null,
  };
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const bookingBasePath = getClassBookingBasePath(location.pathname);
  const isParentFlow = bookingBasePath.startsWith("/parent/");
  const { user } = useSelector((state) => state.user);
  const displayName =
    user?.user_name ||
    user?.full_name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    "";
  const bookingsQuery = useQuery({
    queryKey: ["student-class-booking-dashboard"],
    queryFn: bookingService.getBookings,
    retry: false,
  });

  const bookingSummary = bookingsQuery.data?.summary || {
    pending: 0,
    upcoming: 0,
    past: 0,
    total: 0,
  };
  const handleRetryPayment = (bookingSummary) => {
    if (!bookingSummary?.bookingId || !bookingSummary?.tutorId) {
      return;
    }

    persistBookingSummary(bookingSummary);
    navigate(
      `${bookingBasePath}/booking?bookingId=${encodeURIComponent(
        bookingSummary.bookingId
      )}&tutorId=${encodeURIComponent(bookingSummary.tutorId)}`
    );
  };
  const upcomingSessions = useMemo(
    () =>
      (bookingsQuery.data?.upcoming || []).map((booking) =>
        mapBookingToSessionCard(booking, displayName, handleRetryPayment)
      ),
    [bookingsQuery.data?.upcoming, displayName, handleRetryPayment]
  );
  const previewUpcomingSessions = upcomingSessions.slice(0, 3);

  const headerProps = {
    heading: "Welcome",
    subheading: "Let's help you excel",
    name: displayName,
    subject: "Mathematics"
  };

  const handleBookTutor = () => {
    navigate(`${bookingBasePath}/bookTutor`);
  };

  const handleMyBookings = () => {
    navigate(`${bookingBasePath}/myBookings`);
  };

  return (
    <PageContainer variant={isParentFlow ? "parent" : "default"}>
      <div
        className={`sticky top-0 z-50 ${
          isParentFlow
            ? "bg-[#f8f3ea]/95"
            : "bg-[#DFF2EB]/70"
        } backdrop-blur-md`}
      >
        <Header
          {...headerProps}
          parentLogoutTone={isParentFlow ? "violet" : "orange"}
        />
      </div>

      <MainContent variant={isParentFlow ? "parent" : "default"}>
        <div className="flex items-center justify-between gap-10">
          <div className="flex-1">
            <h1
              className={`text-5xl font-bold ${
                isParentFlow ? "text-[#0B2B66]" : "text-gray-900"
              }`}
            >
              {isParentFlow ? "Parent Booking Dashboard" : "Need extra help?"}
            </h1>

            <p className="text-lg mt-4 text-gray-600 max-w-xl leading-relaxed">
              {isParentFlow
                ? "Use your child's student account to manage tutoring sessions, review bookings and book the next lesson."
                : "Browse qualified Australian maths tutors and book a personalised one-on-one session with an expert teacher."}
            </p>

            <button 
              className={`mt-6 px-10 py-3 shadow-md rounded-full font-semibold border transition ${
                isParentFlow
                  ? "border-[#08059B] bg-[#08059B] text-white hover:bg-[#01044F]"
                  : "border-gray-300 bg-white hover:shadow-lg"
              }`}
              onClick={handleBookTutor}
            >
              Book a Tutor
            </button>
          </div>

          <div className="flex-1 flex justify-end">
            <img
              src={CLASS_BOOKING.heroImage}
              alt="Hero Illustration"
              className="w-[420px] h-auto object-contain"
            />
          </div>
        </div>

        <div className="mt-16 flex gap-10 w-full">
          <div className="flex-1 bg-white rounded-3xl shadow-md p-8 flex items-center gap-6 border border-gray-100">
            <div className="w-16 h-16 rounded-full bg-[#D7DDFF] flex items-center justify-center">
              <img
                src={upcomingIcon}
                alt="Upcoming sessions"
                className="w-8 h-8 object-contain"
              />
            </div>

            <div className="flex flex-col">
              <p className="text-lg text-gray-700 font-medium">
                Upcoming sessions
              </p>
              <h2 className="text-4xl font-semibold text-gray-900">
                {bookingsQuery.isLoading ? "..." : bookingSummary.upcoming}
              </h2>
            </div>
          </div>

          <div className="flex-1 bg-white rounded-3xl shadow-md p-8 flex items-center gap-6 border border-gray-100">
            <div className="w-16 h-16 rounded-full bg-[#FFF6D8] flex items-center justify-center">
              <TrendingUp
                size={28}
                strokeWidth={2.4}
                className="text-amber-600"
              />
            </div>

            <div className="flex flex-col">
              <p className="text-lg text-gray-700 font-medium">
                Pending sessions
              </p>
              <h2 className="text-4xl font-semibold text-gray-900">
                {bookingsQuery.isLoading ? "..." : bookingSummary.pending}
              </h2>
            </div>
          </div>

          <div className="flex-1 bg-white rounded-3xl shadow-md p-8 flex items-center gap-6 border border-gray-100">
            <div className="w-16 h-16 rounded-full bg-[#E8FFE8] flex items-center justify-center">
              <TrendingUp
                size={28}
                strokeWidth={2.4}
                className="text-green-600"
              />
            </div>

            <div className="flex flex-col">
              <p className="text-lg text-gray-700 font-medium">
                Completed sessions
              </p>
              <h2 className="text-4xl font-semibold text-gray-900">
                {bookingsQuery.isLoading ? "..." : bookingSummary.past}
              </h2>
            </div>
          </div>
        </div>

        <div className="bg-transparent mt-16 p-10">
          <div className="flex justify-between items-center mb-10">
            <h3 className="text-2xl font-semibold text-gray-900">
              Upcoming Sessions ({bookingsQuery.isLoading ? "..." : bookingSummary.upcoming})
            </h3>
            <button 
              className="text-blue-700 font-medium hover:underline"
              onClick={handleMyBookings}
            >
              See all my bookings
            </button>
          </div>

          <div className="flex flex-col gap-8 mb-14">
            {bookingsQuery.isError ? (
              <div className="rounded-3xl border border-red-200 bg-red-50 px-6 py-5 text-red-700">
                {getClassBookingQueryErrorMessage(bookingsQuery.error, "bookings")}
              </div>
            ) : bookingsQuery.isLoading ? (
              <div className="rounded-3xl border border-gray-200 bg-white px-6 py-5 text-gray-600">
                Loading bookings...
              </div>
            ) : previewUpcomingSessions.length ? (
              previewUpcomingSessions.map((session) => (
                <SessionCard key={session.id} session={session} />
              ))
            ) : (
              <div className="rounded-3xl border border-gray-200 bg-white px-6 py-5 text-gray-600">
                No upcoming bookings yet.
              </div>
            )}
          </div>

        </div>
      </MainContent>
    </PageContainer>
  );
};

export default Dashboard;
